import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateParam, useTranslate } from "helpers";
import { Routes as FleetFireExtinguisherRoutes } from "ui/modules/fleet/fireExtinguishers/navigation";
import { getOverviewFireExtinguisherExpiringAPI } from "services/api/dashboard/api";
import { OverviewMonthlyExpiringResponse } from "services/api/dashboard/types";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import CardHeader from "../../common/CardHeader";
import BarTooltip, { BarTooltipRow, BarTooltipTitle } from "../../common/HoverPopup";
import { compact, text } from "../../common/dashboardPalette";

const FireExtinguisherExpiringCard: React.FC = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [data, setData] = useState<OverviewMonthlyExpiringResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Klik na mjesec -> pretraga isteka vatrogasnih aparata za taj kalendarski mjesec.
  const handleBarClick = (month: string) => {
    const [mm, yyyy] = month.split(/[/.]/).map(Number);
    if (!mm || !yyyy) return;
    const from = new Date(yyyy, mm - 1, 1);
    const to = new Date(yyyy, mm, 0);
    const params = new URLSearchParams({
      expirationDateFrom: formatDateParam(from)!,
      expirationDateTo: formatDateParam(to)!,
    });
    navigate(`${FleetFireExtinguisherRoutes.FireExtinguisherSearchPageRouter}?${params.toString()}`);
  };

  useEffect(() => {
    getOverviewFireExtinguisherExpiringAPI()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) return <div className="relative h-full"><Loader isLoading={true}> </Loader></div>;
  if (!data) return <div className="flex items-center justify-center h-full text-sm text-slate-400">Nema podataka</div>;

  const months = data.months;
  const maxCount = Math.max(...months.map((m) => m.count), 1);
  const total = data.total;

  // Prva kolona = žuti gradient (jak na vrhu -> svjetliji prema dnu) + 2px border na vrhu kao akcent.
  // Iste boje kao InsuranceExpiringCard / RegistrationExpiringCard.
  const currentBar = "bg-gradient-to-b from-yellow-300 to-yellow-100 border-t-2 border-yellow-400 group-hover/bar:from-yellow-200 group-hover/bar:to-yellow-50";
  const otherBar = "bg-slate-200 group-hover/bar:bg-yellow-200";

  return (
    <div className={`rounded-sm ${compact.padding} bg-white h-full flex flex-col`}>
      <CardHeader
        title="Vatrogasni aparati"
        subtitle="Istek u sljedećih 6 mjeseci"
        className="mb-10"
        right={<span className={`${compact.metric} font-bold ${text.primary}`}>{total}</span>}
      />

      {data.total === 0 ? (
        <NoResults title="Nema isteka aparata u narednih 6 mjeseci" />
      ) : (
        <>
      <div className="mx-auto mb-3 w-full flex-1 flex flex-col">
        <div className="flex justify-between gap-2 mb-2 flex-1 min-h-[110px]">
          {months.map((m, i) => {
            const isCurrent = i === 0;
            const heightPercent = maxCount > 0 ? (m.count / maxCount) * 100 : 0;
            return (
              <div
                key={m.month}
                className="flex flex-col items-center gap-1 flex-1 h-full cursor-pointer group/bar relative"
                onClick={() => handleBarClick(m.month)}
              >
                <span className={`text-sm ${text.muted}`}>{m.count}</span>
                <div className="w-full flex-1 flex items-end">
                  <div
                    className={`w-full rounded-sm origin-bottom animate-growUp transition-all ${isCurrent ? currentBar : otherBar}`}
                    style={{ height: `${Math.max(heightPercent, 5)}%` }}
                  />
                </div>
                <BarTooltip>
                  <BarTooltipTitle>{m.month}</BarTooltipTitle>
                  <BarTooltipRow label="Aparati" value={m.count} />
                </BarTooltip>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between gap-2">
          {months.map((m) => (
            <span key={m.month} className={`text-sm ${text.subtle} flex-1 text-center`}>{m.month.split("/")[0]}</span>
          ))}
        </div>
      </div>

      <div className={`text-sm ${text.muted} text-center`}>
        Istječe u narednih 6 mjeseci
      </div>
        </>
      )}
    </div>
  );
};

export default FireExtinguisherExpiringCard;
